import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import CardTitleRow from './CardTitleRow';
import LoadErrorScreen from './LoadErrorScreen';
import './ItemList.css';
import './ItemCard.css';

const API = process.env.REACT_APP_SERVER_URL;

function normalizePhotos(photos) {
  if (!photos) return [];
  if (Array.isArray(photos)) {
    return photos.map((p) => (typeof p === 'string' ? p : p?.url)).filter(Boolean);
  }
  return [];
}

function getFirstProductPhoto(product) {
  if (!product?.photos?.length) return null;
  const p = product.photos[0];
  return typeof p === 'string' ? p : p?.url;
}

function getPhotoUrl(photo) {
  if (!photo || typeof photo !== 'string') return null;
  const t = photo.trim();
  if (!t) return null;
  if (t.startsWith('http://') || t.startsWith('https://')) return t;
  if (t.startsWith('/')) return `${API}${t}`;
  return `${API}/${t}`;
}

function getFirstTourPhoto(tour) {
  const photos = tour?.photos;
  if (!photos?.length) return null;
  for (const p of photos) {
    const url = getPhotoUrl(p);
    if (url) return url;
  }
  return null;
}

function getFirstServicePhoto(service) {
  const photos = service?.photos;
  if (!photos?.length) return null;
  for (const p of photos) {
    const url = getPhotoUrl(p);
    if (url) return url;
  }
  return null;
}

function CardLabel({ label, bgColor, color = '#000' }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 8,
        left: 8,
        zIndex: 1,
        backgroundColor: bgColor,
        color,
        px: 1,
        py: 0.25,
        borderRadius: '8px',
        fontSize: '0.75rem',
        fontWeight: 600,
      }}
    >
      {label}
    </Box>
  );
}

export default function FavoritesPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [tours, setTours] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { state: { redirectTo: '/favorites' } });
      return;
    }

    const load = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const res = await fetch(`${API}/api/profile/favorites`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 401) {
          navigate('/login', { state: { redirectTo: '/favorites' } });
          return;
        }
        if (!res.ok) throw new Error('Не удалось загрузить избранное');
        const data = await res.json();
        setItems(data.favorites || []);
        setProducts(data.favoriteProducts || []);
        setTours(data.favoriteTours || []);
        setServices(data.favoriteServices || []);
      } catch (err) {
        console.error(err);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [navigate]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: '#8B7D6B' }} />
      </Box>
    );
  }

  if (loadError) {
    return <LoadErrorScreen />;
  }

  const visibleItems = items;
  const visibleProducts = products;
  const visibleTours = tours;
  const visibleServices = services;
  const isEmpty =
    visibleItems.length === 0
    && visibleProducts.length === 0
    && visibleTours.length === 0
    && visibleServices.length === 0;

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden', px: 0.25, mx: 0, mt: 2 }}>
      <Typography variant="h6" sx={{ px: 1, mb: 1, fontWeight: 600 }}>
        Избранное
      </Typography>

      {isEmpty ? (
        <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
          <FavoriteBorderIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography color="text.secondary" gutterBottom>
            Здесь пока пусто
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Нажмите на сердечко на карточке, чтобы сохранить позицию
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={1} className="items-grid-container" sx={{ px: 0.5, overflowX: 'hidden', maxWidth: '100%' }}>
          {visibleItems.map((item) => {
            const photos = normalizePhotos(item.photos);
            const photoUrl = photos[0] || 'https://via.placeholder.com/300';
            return (
              <Grid item xs={6} sm={6} md={4} lg={true} key={`item-${item._id}`} className="item-grid-item">
                <Card component={Link} to={`/item/${item.inventoryNumber || item._id}`} className="card-container">
                  <Box sx={{ position: 'relative' }}>
                    <CardLabel label="Аренда" bgColor="#F9E79F" />
                    <div className="slider-image-container">
                      <img
                        src={photoUrl}
                        alt={item.title || 'Товар'}
                        className="card-image"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/300'; }}
                      />
                    </div>
                  </Box>
                  <CardContent className="card-content">
                    <CardTitleRow type="item" entityId={item._id}>
                      {item.title}
                    </CardTitleRow>
                    <Typography className="card-price" sx={{ fontWeight: 'bold', color: '#2e7d32', fontSize: '0.85rem' }}>
                      {item.price} ₽/сутки
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}

          {visibleProducts.map((product) => {
            const photoUrl = getFirstProductPhoto(product) || 'https://via.placeholder.com/300';
            return (
              <Grid item xs={6} sm={6} md={4} lg={true} key={`product-${product._id}`} className="item-grid-item">
                <Card
                  component={Link}
                  to={`/products/${product.productNumber}`}
                  className="card-container"
                  sx={{ textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column', height: '100%' }}
                >
                  <Box sx={{ position: 'relative' }}>
                    <CardLabel label="Продажа" bgColor="#2e7d32" color="#fff" />
                    <div className="slider-image-container">
                      <img
                        src={photoUrl}
                        alt={product.title || 'Товар'}
                        className="card-image"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/300'; }}
                      />
                    </div>
                  </Box>
                  <CardContent className="card-content">
                    <CardTitleRow type="product" entityId={product._id}>
                      {product.title}
                    </CardTitleRow>
                    <Typography className="card-price" sx={{ fontWeight: 'bold', color: '#2e7d32', fontSize: '0.85rem' }}>
                      {product.price != null ? `${Number(product.price).toLocaleString('ru-RU')} ₽` : 'Цена по запросу'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}

          {visibleTours.map((tour) => {
            const photoUrl = getFirstTourPhoto(tour) || 'https://via.placeholder.com/300';
            return (
              <Grid item xs={6} sm={6} md={4} lg={true} key={`tour-${tour._id}`} className="item-grid-item">
                <Card
                  component={Link}
                  to={`/tours/${tour.tourNumber}`}
                  className="card-container"
                  sx={{ textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column', height: '100%' }}
                >
                  <Box sx={{ position: 'relative' }}>
                    <CardLabel label="Тур" bgColor="#5B7DB1" color="#fff" />
                    <div className="slider-image-container">
                      <img
                        src={photoUrl}
                        alt={tour.title || 'Тур'}
                        className="card-image"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/300'; }}
                      />
                    </div>
                  </Box>
                  <CardContent className="card-content">
                    <CardTitleRow type="tour" entityId={tour._id}>
                      {tour.title}
                    </CardTitleRow>
                    <Typography className="card-price" sx={{ fontWeight: 'bold', color: '#2e7d32', fontSize: '0.85rem' }}>
                      {tour.price != null ? `${Number(tour.price).toLocaleString('ru-RU')} ₽ / место` : 'Цена по запросу'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}

          {visibleServices.map((service) => {
            const photoUrl = getFirstServicePhoto(service) || 'https://via.placeholder.com/300';
            return (
              <Grid item xs={6} sm={6} md={4} lg={true} key={`service-${service._id}`} className="item-grid-item">
                <Card
                  component={Link}
                  to={`/services/${service.serviceId}`}
                  className="card-container"
                  sx={{ textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column', height: '100%' }}
                >
                  <Box sx={{ position: 'relative' }}>
                    <CardLabel label="Услуга" bgColor="#EB984E" />
                    <div className="slider-image-container">
                      <img
                        src={photoUrl}
                        alt={service.name || 'Услуга'}
                        className="card-image"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/300'; }}
                      />
                    </div>
                  </Box>
                  <CardContent className="card-content">
                    <CardTitleRow type="service" entityId={service._id}>
                      {service.name}
                    </CardTitleRow>
                    <Typography className="card-price" sx={{ fontWeight: 'bold', color: '#2e7d32', fontSize: '0.85rem' }}>
                      от {service.minPrice != null ? Number(service.minPrice).toLocaleString('ru-RU') : '—'} ₽
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}
